import React from 'react';
import { Button, Input, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { POSPaymentMethod, POSPaymentSplitLine } from '../../types/pos';
import { formatNu } from '../../utils/formatAmount';
import {
  MobilePayAccount,
  MobilePayQrCompact,
} from './MobileTransferPayPanel';
import './MobileTransferPayPanel.css';

const { Text } = Typography;

const METHOD_OPTIONS = [
  { value: POSPaymentMethod.CASH, label: 'Cash' },
  { value: POSPaymentMethod.MOBILE_TRANSFER, label: 'Mobile (Bank App)' },
  { value: POSPaymentMethod.MANUAL_CARD, label: 'Manual Card' },
];

export function createSplitPaymentLine(method: POSPaymentMethod = POSPaymentMethod.CASH): POSPaymentSplitLine {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    method,
    amount: '',
    reference: '',
    phone: '',
  };
}

export function paymentMethodLabel(method: POSPaymentMethod | string): string {
  const m = String(method || '').toUpperCase();
  switch (m) {
    case POSPaymentMethod.CREDIT:
    case 'CREDIT':
      return 'Credit';
    case POSPaymentMethod.CASH:
    case 'CASH':
      return 'Cash';
    case POSPaymentMethod.MOBILE_TRANSFER:
    case 'MOBILE_TRANSFER':
    case 'BANK_TRANSFER':
      return 'Mobile (Bank App)';
    case POSPaymentMethod.MANUAL_CARD:
    case POSPaymentMethod.CARD_PAYMENT:
    case 'CARD':
    case 'CARD_PAYMENT':
    case 'MANUAL_CARD':
      return 'Card';
    case 'SPLIT':
      return 'Split payment';
    default:
      return String(method);
  }
}

export function paymentMethodShortLabel(method: POSPaymentMethod | string): string {
  const m = String(method || '').toUpperCase();
  if (m === 'CASH') return 'Cash';
  if (m === 'BANK_TRANSFER' || m === 'MOBILE_TRANSFER') return 'App';
  if (m.includes('CARD')) return 'Card';
  if (m === 'SPLIT') return 'Split';
  if (m === 'CREDIT') return 'Credit';
  return paymentMethodLabel(method);
}

export function paymentMethodTagColor(method: POSPaymentMethod | string): string {
  const m = String(method || '').toUpperCase();
  if (m === 'CASH') return 'green';
  if (m === 'SPLIT') return 'gold';
  if (m === 'BANK_TRANSFER' || m === 'MOBILE_TRANSFER') return 'cyan';
  if (m.includes('CARD')) return 'blue';
  return 'default';
}

type SplitPaymentEditorProps = {
  lines: POSPaymentSplitLine[];
  onChange: (lines: POSPaymentSplitLine[]) => void;
  orderTotal: number;
  disabled?: boolean;
  mobilePayAccount?: MobilePayAccount;
};

export const SplitPaymentEditor: React.FC<SplitPaymentEditorProps> = ({
  lines,
  onChange,
  orderTotal,
  disabled,
  mobilePayAccount,
}) => {
  const allocated = lines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
  const remaining = Math.round((orderTotal - allocated) * 1000) / 1000;
  const isBalanced = Math.abs(remaining) < 0.01;

  const updateLine = (id: string, patch: Partial<POSPaymentSplitLine>) => {
    onChange(lines.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const removeLine = (id: string) => {
    if (lines.length <= 2) return;
    onChange(lines.filter((line) => line.id !== id));
  };

  const addLine = () => {
    onChange([...lines, createSplitPaymentLine(POSPaymentMethod.CASH)]);
  };

  return (
    <div className="pos-split-pay">
      <div className="pos-split-pay-head">
        <Text className="pos-split-pay-title">Split payment</Text>
        <Text className="pos-split-pay-total app-mono">
          Order total: {formatNu(orderTotal)}
        </Text>
      </div>

      <div className="pos-split-pay-lines">
        {lines.map((line, index) => {
          const needsRef = line.method === POSPaymentMethod.MOBILE_TRANSFER;
          return (
            <div key={line.id} className="pos-split-pay-line">
              <div className="pos-split-pay-line-top">
                <Text className="pos-split-pay-line-num">Payment {index + 1}</Text>
                {lines.length > 2 && (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={disabled}
                    onClick={() => removeLine(line.id)}
                    aria-label="Remove payment line"
                  />
                )}
              </div>
              <Select
                className="pos-split-pay-method"
                value={line.method}
                disabled={disabled}
                options={METHOD_OPTIONS}
                onChange={(value) => updateLine(line.id, { method: value as POSPaymentMethod })}
              />
              <div className="pos-split-pay-amount-field">
                <span className="pos-split-pay-amount-prefix">Nu.</span>
                <Input
                  className="pos-split-pay-amount-input app-mono"
                  placeholder="0.000"
                  value={line.amount}
                  disabled={disabled}
                  bordered={false}
                  onChange={(e) => updateLine(line.id, { amount: e.target.value })}
                />
              </div>
              {needsRef && mobilePayAccount ? (
                <MobilePayQrCompact
                  amountNu={parseFloat(line.amount) || 0}
                  account={mobilePayAccount}
                />
              ) : null}
              {needsRef && (
                <Input
                  className="pos-split-pay-ref"
                  placeholder="Bank journal number (required)"
                  value={line.reference}
                  disabled={disabled}
                  onChange={(e) => updateLine(line.id, { reference: e.target.value })}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="pos-split-pay-footer">
        <Button type="dashed" block icon={<PlusOutlined />} disabled={disabled} onClick={addLine}>
          Add payment method
        </Button>
        <div className={`pos-split-pay-balance${isBalanced ? ' pos-split-pay-balance--ok' : ''}`}>
          <span>Allocated: {formatNu(allocated)}</span>
          <span className={remaining > 0.01 ? 'pos-split-pay-remaining--due' : ''}>
            {isBalanced ? 'Balanced' : `Remaining: ${formatNu(remaining)}`}
          </span>
        </div>
      </div>
    </div>
  );
};

export function validateSplitPaymentLines(
  lines: POSPaymentSplitLine[],
  orderTotal: number,
): string | null {
  if (lines.length < 2) {
    return 'Split payment needs at least two payment methods';
  }
  let sum = 0;
  for (const line of lines) {
    const amount = parseFloat(line.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return 'Enter a valid amount for each payment method';
    }
    if (line.method === POSPaymentMethod.MOBILE_TRANSFER && !line.reference.trim()) {
      return 'Bank journal number is required for mobile (bank app) payments';
    }
    sum += amount;
  }
  if (Math.abs(sum - orderTotal) > 0.01) {
    return `Payment split (${formatNu(sum)}) must equal order total (${formatNu(orderTotal)})`;
  }
  return null;
}
